import { useEffect, useState } from "react";
import { api, type Team , errorText } from "../api.js";
import { swr, invalidate, queueMatch } from "../outbox.js";
import { Screen, Card, Row, Button, Field, Empty, Msg, SectionLabel, Badge, Skeleton } from "../ui.js";
import { Toss, type TossResult } from "./Toss.js";
import { IconPlus, IconTrash, IconChevron } from "../Icons.js";

interface MatchRow {
  id: string; matchId: string; status: string; scheduledAt?: number | null;
  homeName: string; homeShort: string; awayName: string; awayShort: string;
}

export function Matches({ clubId, canScore, onOpen }: {
  clubId: string; canScore: boolean; onOpen: (matchId: string) => void;
}) {
  const [rows, setRows] = useState<MatchRow[] | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);


  const load = async () => {
    const hitM = await swr(`matches:${clubId}`,
      () => api<{ matches: MatchRow[] }>(`/api/v1/clubs/${clubId}/matches`),
      (d) => setRows(d.matches));
    if (hitM) setRows(hitM.matches);
    const hitC = await swr(`club:${clubId}`,
      () => api<{ teams: Team[] }>(`/api/v1/clubs/${clubId}`),
      (d) => setTeams(d.teams));
    if (hitC) setTeams(hitC.teams);
  };
  useEffect(() => { void load(); }, [clubId]);


  const remove = async (m: MatchRow) => {
    const live = m.status === "LIVE";
    const warn = live
      ? `${m.homeShort} v ${m.awayShort} is LIVE. Deleting it removes the fixture and its statistics. Continue?`
      : `Delete ${m.homeShort} v ${m.awayShort}? Its statistics go with it.`;
    if (!confirm(warn)) return;
    const r = await api<{ error?: string; statsRemoved?: number }>(`/api/v1/fixtures/${m.id}`,
      { method: "DELETE", json: { force: live } });
    if (r.ok) {
      invalidate(`matches:${clubId}`, `club:${clubId}`);
      void load();
    } else setMsg(errorText(r.data, r.status));
  };

  return (
    <Screen title="Matches" sub={rows ? `${rows.length} played or in progress` : undefined}
      action={canScore ? <Button onClick={() => setCreating((v) => !v)}>
            <span className="flex items-center gap-1.5">
              {!creating && <IconPlus size={15} />}{creating ? "Cancel" : "New"}
            </span>
          </Button> : undefined}>
      <Msg text={msg} kind="err" />
      {creating && <QuickMatch clubId={clubId} teams={teams}
        onStarted={(m) => { setCreating(false); onOpen(m); }} onError={setMsg} />}

      {!rows ? <Card><Skeleton rows={4} /></Card>
      : rows.length === 0 ? <Card><Empty title="No matches yet"
              {...(canScore ? { action: { label: "Start a match", onClick: () => setCreating(true) } } : {})}>
              Score one now, or schedule it in a tournament.
            </Empty></Card>
      : (
        <Card>
          {rows.map((m) => (
            <Row key={m.id} onClick={() => onOpen(m.matchId)}>
              <div className="min-w-0 flex-1">
                <b className="block truncate font-semibold">{m.homeShort} v {m.awayShort}</b>
                {m.scheduledAt && (
                  <span className="font-mono text-caption text-faint">
                    {new Date(m.scheduledAt).toLocaleDateString("en-GB",
                      { weekday: "short", day: "numeric", month: "short", timeZone: "Asia/Karachi" })}
                  </span>
                )}
              </div>
              <Badge status={m.status} pulse={m.status === "LIVE"} />
              {canScore && (
                <span onClick={(e) => { e.stopPropagation(); void remove(m); }}
                  aria-label="Delete this match"
                  className="flex h-11 w-11 items-center justify-center rounded-full border
                             border-line text-faint hover:border-leather hover:text-leather">
                  <IconTrash size={16} />
                </span>
              )}
              <span className="text-faint"><IconChevron /></span>
            </Row>
          ))}
        </Card>
      )}
    </Screen>
  );
}

function QuickMatch({ clubId, teams, onStarted, onError }: {
  clubId: string; teams: Team[];
  onStarted: (matchId: string) => void; onError: (m: string) => void;
}) {
  const [home, setHome] = useState(""); const [away, setAway] = useState("");
  const [overs, setOvers] = useState("6");
  /**
   * Which tournament this game belongs to, if any.
   *
   * A match had no way to say it was a Cup game. So a tournament's rules
   * governed nothing that was actually played under them, and its table
   * counted nothing — the column has been in the fixtures schema since the
   * first migration and nothing ever set it.
   */
  const [cups, setCups] = useState<{ id: string; name: string; status: string }[]>([]);
  const [cupId, setCupId] = useState("");

  useEffect(() => {
    void api<{ tournaments: { id: string; name: string; status: string }[] }>(
      `/api/v1/clubs/${clubId}/tournaments`).then((r) => {
        if (r.ok) setCups(r.data.tournaments.filter((t) => t.status !== "ARCHIVED"));
      });
  }, [clubId]);
  const [tossing, setTossing] = useState(false);
  const [busy, setBusy] = useState(false);

  const go = async (t: TossResult) => {
    setTossing(false); setBusy(true);

    /**
     * Name the match on the phone, not on the server.
     *
     * A scorer at a ground with no signal has to be able to start a game. If
     * the id came from the server the match could not exist until the phone
     * was online, and by then the first over has been played on paper.
     *
     * The id is random hex either way, so the phone can generate it. The
     * server accepts a client-supplied one and is idempotent on it, which is
     * what makes the offline path safe: the same request arriving twice
     * creates one fixture, not two.
     */
    const matchId = `mt_${[...crypto.getRandomValues(new Uint8Array(10))]
      .map((b) => b.toString(16).padStart(2, "0")).join("")}`;
    const body = {
      matchId, homeTeamId: home, awayTeamId: away, tossWinnerTeamId: t.winnerTeamId,
      tossDecision: t.decision, overs: Number(overs) || 6,
      ...(cupId ? { tournamentId: cupId } : {}),
    };

    if (!navigator.onLine) {
      await queueMatch(clubId, matchId, body);
      setBusy(false);
      onStarted(matchId);
      return;
    }

    const r = await api<{ matchId?: string; error?: string }>(
      `/api/v1/clubs/${clubId}/quick-match`, { json: body });
    setBusy(false);
    if (r.ok && r.data.matchId) { onStarted(r.data.matchId); return; }

    // Online but the request failed — a dead spot rather than a refusal. Queue
    // it and let the scorer get on with the game.
    if (r.status === 0 || r.status >= 500) {
      await queueMatch(clubId, matchId, body);
      onStarted(matchId);
      return;
    }
    onError(errorText(r.data, r.status));
  };

  const pick = (val: string, set: (v: string) => void, exclude?: string) => (
    <div className="grid grid-cols-2 gap-2">
      {teams.filter((t) => t.id !== exclude).map((t) => (
        <button key={t.id} onClick={() => set(t.id)}
          className={`rounded-card border px-3 py-3 text-left text-body font-semibold
            ${val === t.id ? "border-amber bg-[#20180C] text-amber" : "border-line bg-raise"}`}>
          {t.shortName} <span className="font-normal text-faint">— {t.name}</span>
        </button>
      ))}
    </div>
  );

  const h = teams.find((t) => t.id === home);
  const a = teams.find((t) => t.id === away);

  return (
    <div className="mb-4 space-y-4 rounded-xl border border-line bg-panel p-4">
      <div>
        <SectionLabel className="mb-1.5 block">Home</SectionLabel>
        {pick(home, (v) => { setHome(v); if (away === v) setAway(""); })}
      </div>
      <div>
        <SectionLabel className="mb-1.5 block">Away</SectionLabel>
        {pick(away, setAway, home)}
      </div>
      {home && away && (
        <>
          {cups.length > 0 && (
            <div>
              <SectionLabel className="mb-1.5 block">Tournament</SectionLabel>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setCupId("")}
                  className={`min-h-[44px] flex-1 rounded-control border px-3 text-caption
                    ${!cupId ? "border-amber bg-[#20180C] text-amber" : "border-line bg-raise"}`}>
                  Friendly
                </button>
                {cups.map((t) => (
                  <button key={t.id} onClick={() => setCupId(t.id)}
                    className={`min-h-[44px] flex-1 rounded-control border px-3 text-caption
                      ${cupId === t.id ? "border-amber bg-[#20180C] text-amber" : "border-line bg-raise"}`}>
                    {t.name}
                  </button>
                ))}
              </div>
              {cupId !== "" && (
                <p className="mt-1.5 text-meta leading-relaxed text-faint">
                  This game plays by the tournament's rules and counts towards its table.
                  Overs come from the tournament.
                </p>
              )}
            </div>
          )}
          {/* Overs only matter for a friendly — a tournament sets its own. */}
          {cupId === "" && (
            <Field label="Overs" inputMode="numeric" value={overs} onChange={(e) => setOvers(e.target.value)} />
          )}
          <Button full disabled={busy} onClick={() => setTossing(true)}>
            {busy ? "Starting…" : "Go to the toss"}
          </Button>
          <p className="text-center text-caption text-faint">
            One side calls, the coin decides, the winner chooses.
          </p>
        </>
      )}

      {tossing && h && a && (
        <Toss
          home={{ id: h.id, name: h.name, short: h.shortName }}
          away={{ id: a.id, name: a.name, short: a.shortName }}
          onDone={go}
          onCancel={() => setTossing(false)}
        />
      )}
    </div>
  );
}
